import { Component, forwardRef, Input, ViewChild, ElementRef, AfterViewInit, OnDestroy, inject, PLATFORM_ID } from '@angular/core';
import {
  NG_VALUE_ACCESSOR, ControlValueAccessor,
  NG_VALIDATORS, Validator,
  AbstractControl, ValidationErrors, FormControl, Validators,
} from '@angular/forms';
import { isPlatformBrowser } from '@angular/common';
import intlTelInput from 'intl-tel-input';
import { Subscription } from 'rxjs';
import { InputReusable } from '../input-reusable/input-reusable';

type CountryData = { dialCode?: string };
type PhoneReusableSize = 'md' | 'lg';

type IntlTelInstance = {
  destroy: () => void;
  setNumber: (value: string) => void;
  getSelectedCountryData?: () => CountryData;
  getNumberPlaceholder?: () => string | undefined;
};

@Component({
  selector: 'app-phone-reusable',
  standalone: true,
  imports: [InputReusable],
  templateUrl: './phone-reusable.html',
 
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => phoneReusable), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => phoneReusable), multi: true },
  ],
})
export class phoneReusable implements ControlValueAccessor, Validator, AfterViewInit, OnDestroy {
  private static readonly dropdownGap = 6;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);

  @ViewChild('phoneWrapper', { static: false }) phoneWrapperRef?: ElementRef<HTMLElement>;
  @Input() label = 'Mobile number';
  @Input() labelClass = 'mb-1 block text-sm font-medium !text-slate900';
  @Input() required = false;
  @Input() placeholder = 'Enter phone number';
  @Input() disabled = false;
  @Input() size: PhoneReusableSize = 'lg';

  readonly phoneControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.pattern(/^\d*$/), Validators.minLength(10), Validators.maxLength(10)],
  });

  private iti: IntlTelInstance | null = null;
  private pendingValue: string | null = null;
  private maxDigits: number | null = null;
  private readonly nationalDigitLimit = 10;
  private suppressInput = false;
  private valueChangesSub: Subscription | null = null;
  private removeCountryChangeListener: (() => void) | null = null;
  private removeDropdownInteractionListeners: (() => void) | null = null;
  private removeInputListeners: (() => void) | null = null;
  private onChange: (value: string) => void = (): void => undefined;
  private onTouched: () => void = (): void => undefined;
  private onValidatorChange: () => void = (): void => undefined;

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }

    const el = this.getPhoneInputElement();
    if (!el) return;

    this.iti = intlTelInput(el, {
      initialCountry: 'gb',
      allowDropdown: true,
      separateDialCode: true,
      autoPlaceholder: 'polite',
      dropdownContainer: document.body,
      loadUtils: () => import('intl-tel-input/utils'),
    }) as IntlTelInstance;

    this.valueChangesSub = this.phoneControl.valueChanges.subscribe(() => {
      this.onInput();
    });

    const onInputKeyDown = (event: KeyboardEvent): void => {
      this.onKeyDown(event);
    };
    const onInputPaste = (event: ClipboardEvent): void => {
      this.onPaste(event);
    };
    el.addEventListener('keydown', onInputKeyDown);
    el.addEventListener('paste', onInputPaste);
    this.removeInputListeners = () => {
      el.removeEventListener('keydown', onInputKeyDown);
      el.removeEventListener('paste', onInputPaste);
    };

    if (this.pendingValue) {
      this.applyIncomingValue(this.pendingValue);
    }
    this.pendingValue = null;
    this.refreshMaxDigits();

    const onCountryChange = (event: Event): void => {
      this.refreshMaxDigits();
      if (el.value.replace(/\D/g, '')) {
        this.truncateToMax();
        this.emit();
      }
      this.onValidatorChange();

      if (!event.isTrusted || this.disabled) {
        return;
      }

      setTimeout(() => {
        if (this.disabled) {
          return;
        }

        const input = this.getPhoneInputElement();
        if (!input) {
          return;
        }

        input.focus();
        const end = input.value.length;
        input.setSelectionRange(end, end);
      });
    };

    el.addEventListener('countrychange', onCountryChange);
    this.removeCountryChangeListener = () => {
      el.removeEventListener('countrychange', onCountryChange);
    };

    const trigger = this.getCountryDropdownTrigger();
    const onSelectedCountryClick = (event: MouseEvent): void => {
      if (!this.isCountryDropdownOpen()) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.closeCountryDropdown();
    };

    const onDocumentKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'ArrowUp' || !this.isCountryDropdownOpen()) {
        return;
      }

      const target = event.target;
      const activeContainer = this.getActiveDropdownContainer();
      const isWithinPhoneField = target instanceof Node && !!this.phoneWrapperRef?.nativeElement.contains(target);
      const isWithinDropdown = target instanceof Node && !!activeContainer?.contains(target);

      if (!isWithinPhoneField && !isWithinDropdown) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.closeCountryDropdown();
    };

    const onOpenCountryDropdown = (): void => {
      setTimeout(() => this.adjustCountryDropdownToVisibleBounds(), 0);
    };

    const onCloseCountryDropdown = (): void => {
      this.resetCountryDropdownPosition();
    };

    trigger?.addEventListener('click', onSelectedCountryClick, true);
    document.addEventListener('keydown', onDocumentKeyDown, true);
    el.addEventListener('open:countrydropdown', onOpenCountryDropdown as EventListener);
    el.addEventListener('close:countrydropdown', onCloseCountryDropdown as EventListener);
    this.removeDropdownInteractionListeners = () => {
      trigger?.removeEventListener('click', onSelectedCountryClick, true);
      document.removeEventListener('keydown', onDocumentKeyDown, true);
      el.removeEventListener('open:countrydropdown', onOpenCountryDropdown as EventListener);
      el.removeEventListener('close:countrydropdown', onCloseCountryDropdown as EventListener);
    };
  }

  ngOnDestroy(): void {
    this.valueChangesSub?.unsubscribe();
    this.removeInputListeners?.();
    this.removeCountryChangeListener?.();
    this.removeDropdownInteractionListeners?.();
    if (this.isBrowser) {
      this.resetCountryDropdownPosition();
    }
    this.iti?.destroy();
  }

  writeValue(value: unknown): void {
    const next = value != null ? String(value) : '';

    if (!this.iti) {
      this.pendingValue = next;
      this.phoneControl.setValue(this.getDisplayDigits(next), { emitEvent: false });
      return;
    }

    this.applyIncomingValue(next);
  }

  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }
  registerOnValidatorChange(fn: () => void): void { this.onValidatorChange = fn; }
  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.phoneControl.disable({ emitEvent: false });
      return;
    }
    this.phoneControl.enable({ emitEvent: false });
  }
  markAsTouched(): void { this.onTouched(); this.onValidatorChange(); }

  public closeCountryDropdownMenu(): void {
    this.closeCountryDropdown();
    this.resetCountryDropdownPosition();
  }

  onInput(): void {
    if (this.suppressInput) return;
    this.truncateToMax();
    this.syncControlFromInput();
    this.emit();
    this.onValidatorChange();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (this.disabled) return;

    if (event.key === 'ArrowUp' && this.isCountryDropdownOpen()) {
      event.preventDefault();
      this.closeCountryDropdown();
      return;
    }

    const nav = ['Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','Tab','Escape','Enter'];
    if (nav.includes(event.key) || event.ctrlKey || event.metaKey) return;
    if (!/^\d$/.test(event.key)) { event.preventDefault(); return; }
    if (!this.maxDigits) return;

    const el = this.getPhoneInputElement();
    if (!el) return;

    const raw = el.value;
    const cur = raw.replace(/\D/g, '').length;
    const selectionStart = el.selectionStart ?? raw.length;
    const selectionEnd = el.selectionEnd ?? raw.length;
    const sel = raw.slice(selectionStart, selectionEnd).replace(/\D/g, '').length;
    if (cur - sel + 1 > this.maxDigits) event.preventDefault();
  }

  onPaste(event: ClipboardEvent): void {
    if (!this.maxDigits || this.disabled) return;
    const el = this.getPhoneInputElement();
    if (!el) return;

    const paste = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '');
    if (!paste) return;

    const raw = el.value;
    const selectionStart = el.selectionStart ?? raw.length;
    const selectionEnd = el.selectionEnd ?? raw.length;
    const before = raw.slice(0, selectionStart).replace(/\D/g, '');
    const after = raw.slice(selectionEnd).replace(/\D/g, '');
    const available = Math.max(this.maxDigits - before.length - after.length, 0);
    const toInsert = paste.slice(0, available);
    if (!toInsert) { event.preventDefault(); return; }

    event.preventDefault();
    this.setNational((before + toInsert + after).slice(0, this.maxDigits));
    this.syncControlFromInput();
    this.emit();
    this.onValidatorChange();
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    void _control;

    if (this.disabled) {
      return null;
    }

    const digits = this.getPhoneInputElement()?.value?.replace(/\D/g, '') ?? '';
    if (!digits) {
      return this.required ? { required: true } : null;
    }

    if (!/^\d+$/.test(digits)) {
      return { pattern: true };
    }

    if (digits.length > this.nationalDigitLimit) {
      return {
        maxlength: { requiredLength: this.nationalDigitLimit, actualLength: digits.length },
      };
    }

    if (digits.length < this.nationalDigitLimit) {
      return {
        minlength: { requiredLength: this.nationalDigitLimit, actualLength: digits.length },
      };
    }

    return null;
  }

  private emit(): void {
    if (!this.iti) return;
    const nationalDigits = this.getPhoneInputElement()?.value.replace(/\D/g, '') ?? '';
    if (!nationalDigits) { this.onChange(''); return; }
    const dialCode = this.iti.getSelectedCountryData?.()?.dialCode ?? '';
    const formatted = dialCode ? `+${dialCode} ${nationalDigits}` : nationalDigits;
    this.onChange(formatted);
  }

  private applyIncomingValue(value: string): void {
    const el = this.getPhoneInputElement();
    if (!el) {
      return;
    }

    const compactValue = value.replace(/\s+/g, '');
    if (!compactValue) {
      el.value = '';
      if (this.phoneControl.value) {
        this.phoneControl.setValue('', { emitEvent: false });
      }
      return;
    }

    const shouldBypassIntlNormalization = !compactValue.trim().startsWith('+')
      || this.extractNationalDigitsFromInternational(compactValue) !== null;

    if (shouldBypassIntlNormalization) {
      const displayDigits = this.getDisplayDigits(compactValue);
      this.suppressInput = true;
      try {
        el.value = displayDigits;
      } finally {
        this.suppressInput = false;
      }

      if (this.phoneControl.value !== displayDigits) {
        this.phoneControl.setValue(displayDigits, { emitEvent: false });
      }
      return;
    }

    this.iti?.setNumber(compactValue);
    this.refreshMaxDigits();
    this.truncateToMax();
    this.syncControlFromInput();
  }

  private getDisplayDigits(rawValue: string): string {
    const compact = rawValue.replace(/\s+/g, '');
    const digits = this.extractNationalDigitsFromInternational(compact) ?? compact.replace(/\D/g, '');
    return digits.slice(0, this.nationalDigitLimit);
  }

  private extractNationalDigitsFromInternational(rawValue: string): string | null {
    if (!rawValue.trim().startsWith('+')) {
      return null;
    }

    const digitsOnly = rawValue.replace(/\D/g, '');
    const selectedDialCode = this.iti?.getSelectedCountryData?.()?.dialCode;
    const candidateDialCodes = [selectedDialCode, '44'].filter((code): code is string => Boolean(code));

    for (const dialCode of candidateDialCodes) {
      if (digitsOnly.startsWith(dialCode)) {
        return digitsOnly.slice(dialCode.length);
      }
    }

    return null;
  }

  private refreshMaxDigits(): void {
    this.maxDigits = this.nationalDigitLimit;
  }

  private truncateToMax(): void {
    const input = this.getPhoneInputElement();
    if (!this.maxDigits || !input) return;
    const digits = input.value.replace(/\D/g, '');
    if (digits.length > this.maxDigits) this.setNational(digits.slice(0, this.maxDigits));
  }

  private setNational(digits: string): void {
    if (!this.iti) return;
    const dialCode = this.iti.getSelectedCountryData?.()?.dialCode;
    this.suppressInput = true;
    try {
      this.iti.setNumber(dialCode ? `+${dialCode}${digits}` : digits);
    } finally {
      this.suppressInput = false;
    }
  }

  private getPhoneInputElement(): HTMLInputElement | null {
    const host = this.phoneWrapperRef?.nativeElement;
    if (!host) {
      return null;
    }

    return host.querySelector('input[type="tel"]')
      ?? host.querySelector('input:not(.iti__search-input)')
      ?? null;
  }

  private syncControlFromInput(): void {
    const input = this.getPhoneInputElement();
    if (!input) {
      return;
    }

    const currentNational = input.value.replace(/\D/g, '').slice(0, this.nationalDigitLimit);
    if (input.value !== currentNational) {
      input.value = currentNational;
    }

    if (this.phoneControl.value !== currentNational) {
      this.phoneControl.setValue(currentNational, { emitEvent: false });
    }
  }

  private isCountryDropdownOpen(): boolean {
    const trigger = this.getCountryDropdownTrigger();
    return trigger?.getAttribute('aria-expanded') === 'true';
  }

  private closeCountryDropdown(): void {
    if (!this.isBrowser) {
      return;
    }

    if (!this.isCountryDropdownOpen()) {
      return;
    }

    document.documentElement.dispatchEvent(new MouseEvent('click', { bubbles: true }));
  }

  private getActiveDropdownContainer(): HTMLElement | null {
    if (!this.isBrowser) {
      return null;
    }

    return document.querySelector<HTMLElement>('.iti--container:not(.iti__hide)');
  }

  private adjustCountryDropdownToVisibleBounds(): void {
    if (!this.isBrowser) {
      return;
    }

    const input = this.getPhoneInputElement();
    const container = this.getActiveDropdownContainer();
    const content = container?.querySelector<HTMLElement>('.iti__dropdown-content');
    const list = container?.querySelector<HTMLElement>('.iti__country-list');
    const searchWrapper = container?.querySelector<HTMLElement>('.iti__search-input-wrapper');
    const boundary = this.getVisibleBoundaryRect();

    if (!input || !container || !content || !boundary) {
      return;
    }

    const inputRect = input.getBoundingClientRect();
    const gap = phoneReusable.dropdownGap;
    const dropdownHeight = content.getBoundingClientRect().height || content.scrollHeight || 260;
    const spaceBelow = Math.max(boundary.bottom - inputRect.bottom - gap, 0);
    const spaceAbove = Math.max(inputRect.top - boundary.top - gap, 0);
    const shouldOpenAbove = dropdownHeight > spaceBelow && spaceAbove > spaceBelow;
    const searchHeight = searchWrapper?.getBoundingClientRect().height ?? 0;
    const availableContentHeight = Math.max(Math.floor((shouldOpenAbove ? spaceAbove : spaceBelow) - searchHeight), 96);

    container.style.top = '';
    container.style.bottom = '';
    container.style.left = `${Math.round(inputRect.left)}px`;
    container.style.width = `${Math.round(inputRect.width)}px`;
    container.style.minWidth = `${Math.round(inputRect.width)}px`;
    content.style.maxHeight = '';
    container.classList.remove('iti--opens-upward');

    if (list) {
      list.style.maxHeight = '';
    }

    if (shouldOpenAbove) {
      container.classList.add('iti--opens-upward');
      container.style.top = 'unset';
      container.style.bottom = `${Math.round(window.innerHeight - inputRect.top + gap)}px`;
      content.style.maxHeight = `${Math.max(Math.floor(spaceAbove), 120)}px`;
      if (list) {
        list.style.maxHeight = `${availableContentHeight}px`;
      }
      return;
    }

    container.style.bottom = 'unset';
    container.style.top = `${Math.round(inputRect.bottom + gap)}px`;
    content.style.maxHeight = `${Math.max(Math.floor(spaceBelow), 120)}px`;
    if (list) {
      list.style.maxHeight = `${availableContentHeight}px`;
    }
  }

  private resetCountryDropdownPosition(): void {
    if (!this.isBrowser) {
      return;
    }

    const container = document.querySelector<HTMLElement>('.iti--container');
    const content = container?.querySelector<HTMLElement>('.iti__dropdown-content');
    const list = container?.querySelector<HTMLElement>('.iti__country-list');

    if (container) {
      container.style.top = '';
      container.style.bottom = '';
      container.style.left = '';
      container.style.width = '';
      container.style.minWidth = '';
      container.classList.remove('iti--opens-upward');
    }

    if (content) {
      content.style.maxHeight = '';
    }

    if (list) {
      list.style.maxHeight = '';
    }
  }

  private getVisibleBoundaryRect(): { top: number; bottom: number } | null {
    if (!this.isBrowser) {
      return null;
    }

    const host = this.phoneWrapperRef?.nativeElement;
    if (!host) {
      return null;
    }

    let top = 0;
    let bottom = window.innerHeight;
    let currentElement: HTMLElement | null = host.parentElement;

    while (currentElement && currentElement !== document.body) {
      const styles = window.getComputedStyle(currentElement);
      const overflowValues = [styles.overflow, styles.overflowY, styles.overflowX];
      const clipsContent = overflowValues.some((value) => ['auto', 'scroll'].includes(value));

      if (clipsContent) {
        const rect = currentElement.getBoundingClientRect();
        top = Math.max(top, rect.top);
        bottom = Math.min(bottom, rect.bottom);
      }

      currentElement = currentElement.parentElement;
    }

    if (bottom <= top) {
      return null;
    }

    return { top, bottom };
  }

  private getCountryDropdownTrigger(): HTMLButtonElement | null {
    const host = this.phoneWrapperRef?.nativeElement;
    if (!host) {
      return null;
    }

    return host.querySelector<HTMLButtonElement>('.iti__selected-country');
  }

  get suffixIconSize(): number {
    return this.size === 'md' ? 20 : 20;
  }
}
